// Must run before any module that builds a zod schema
import '../config/zodJitless'
import '../styles/globals.css'
import '@omnea-app/fe-legacy/components/layout/wrapper/beamer.css'
import '@fontsource-variable/inter'
import '@fontsource/ibm-plex-mono/400.css'
import { datadogRum } from '@datadog/browser-rum'
import { LicenseInfo } from '@mui/x-license'
import { SessionProvider } from 'next-auth/react'
import { AppProps } from 'next/app'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { type ReactElement, Suspense, useEffect } from 'react'
import { analytics } from '@omnea-app/fe-analytics/client'
import { SessionAnalytics } from '@omnea-app/fe-analytics/SessionAnalytics'
import { type NextPageWithLayout } from '@omnea-app/fe-app-shell/NextPageWithLayout'
import { Auth } from '@omnea-app/fe-auth/Auth'
import { OrganisationSwitchBoundary } from '@omnea-app/fe-auth/OrganisationSwitchBoundary'
import { TokenProvider } from '@omnea-app/fe-auth/TokenProvider'
import { usePreventNumberInputScroll } from '@omnea-app/fe-browser-utils/usePreventNumberInputScroll'
import { FullWidthLoadingFallback } from '@omnea-app/fe-render-boundaries/LoadingFallbacks'
import { OrganisationMismatchProvider } from '@omnea-app/fe-render-boundaries/OrganisationMismatch'
import { SnackbarProvider } from '@omnea-app/omui/SnackbarProvider'
import { ThemeProvider } from '@omnea-app/omui/ThemeProvider'
import { useJsBundleInvalidation } from '../config/useJsBundleInvalidation'
import { SessionMeticulous } from '../meticulous/SessionMeticulous'
import { installDDFetchErrorCapture } from '../observability/ddFetchErrorCapture'
import { AppTranslationsProvider, AuthenticatedAppProviders, OrgScopedReactQueryProvider } from '../providers/AppProviders'
import { AppWrapper } from '../shell/AppWrapper'
import { PageTitleProvider } from '../shell/page-metadata/PageTitleProvider'
import UpTimePage from './uptime'
import type { GatedRoute } from '@omnea-app/fe-authz/RouteAccessGate'

// oxlint-disable-next-line @typescript-eslint/no-non-null-assertion -- FIXME: bulk migration to add error
LicenseInfo.setLicenseKey(process.env.MUI_LICENSE_KEY!)

const NEXT_PUBLIC_DD_RUM_APPLICATION_ID = process.env.NEXT_PUBLIC_DD_RUM_APPLICATION_ID
const NEXT_PUBLIC_DD_RUM_CLIENT_TOKEN = process.env.NEXT_PUBLIC_DD_RUM_CLIENT_TOKEN
const NEXT_PUBLIC_DD_SITE = process.env.NEXT_PUBLIC_DD_SITE ?? 'datadoghq.eu'

if (NEXT_PUBLIC_DD_RUM_APPLICATION_ID && NEXT_PUBLIC_DD_RUM_CLIENT_TOKEN) {
  datadogRum.init({
    applicationId: NEXT_PUBLIC_DD_RUM_APPLICATION_ID,
    clientToken: NEXT_PUBLIC_DD_RUM_CLIENT_TOKEN,
    site: NEXT_PUBLIC_DD_SITE,
    service: 'omnea-app',
    env: process.env.OMNEA_APP_ENV,
    version: process.env.NEXT_PUBLIC_BUNDLE_ID ?? 'unknown',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    traceSampleRate: 50,
    trackBfcacheViews: true,
    trackResources: true,
    defaultPrivacyLevel: 'mask',
    allowedTracingUrls: process.env.API_ROOT_URL ? [process.env.API_ROOT_URL] : [],
  })
  installDDFetchErrorCapture()
}

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout
}

const App = ({ Component, pageProps: { session: serverSession, ...pageProps }, ...otherProps }: AppPropsWithLayout) => {
  useJsBundleInvalidation()
  usePreventNumberInputScroll()

  const router = useRouter()
  useEffect(() => {
    const handleRouteChange = (url: string) => {
      void analytics.then((analytics) => analytics.page(router.pathname, { url }))
    }

    router.events.on('routeChangeComplete', handleRouteChange)

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router])

  const getLayout = Component.getLayout ?? ((page: ReactElement) => page)

  const gatedRoute: GatedRoute = {
    pattern: otherProps.router.route,
    query: otherProps.router.query,
    isQueryResolved: otherProps.router.isReady,
  }

  // DO NOT REMOVE
  if (otherProps.router.route === '/uptime') {
    return <UpTimePage />
  }

  return (
    <>
      <Head>
        <title>Omnea</title>
      </Head>
      <SessionProvider
        session={serverSession}
        // Disable built-in refresh - TokenProvider handles visibility-aware refresh
        refetchOnWindowFocus={false}
        refetchInterval={0}
      >
        <SessionAnalytics />
        <SessionMeticulous />
        <TokenProvider>
          <Auth>
            <ThemeProvider>
              <OrgScopedReactQueryProvider>
                <Suspense fallback={<FullWidthLoadingFallback />}>
                  <AppTranslationsProvider>
                    <SnackbarProvider>
                      <PageTitleProvider>
                        <OrganisationMismatchProvider component={OrganisationSwitchBoundary}>
                          <AuthenticatedAppProviders>
                            <AppWrapper
                              disableSidebar={Component.disableSidebar}
                              sidebarCollapsedByDefault={Component.sidebarCollapsedByDefault}
                              route={gatedRoute}
                            >
                              {getLayout(<Component {...pageProps} />)}
                            </AppWrapper>
                          </AuthenticatedAppProviders>
                        </OrganisationMismatchProvider>
                      </PageTitleProvider>
                    </SnackbarProvider>
                  </AppTranslationsProvider>
                </Suspense>
              </OrgScopedReactQueryProvider>
            </ThemeProvider>
          </Auth>
        </TokenProvider>
      </SessionProvider>
    </>
  )
}

export default App
